import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import _isEmpty from 'lodash/isEmpty';

import { bem } from 'helpers/class-name';
import { manualSchedulesSummaryPreviewPropTypes } from 'constants/propTypes';
import { makePath } from 'helpers/link-generation';
import Button from 'components/Button';
import Message from 'components/Message';
import LastUpdate from 'components/LastUpdate';
import SchedulePreviewActions from './SchedulePreviewActions';
import ScheduleRecurrenceDays from '../ScheduleRecurrenceDays';
import { DRAFT_STATUS, CANCELLED_STATUS } from '../../Schedules.helpers';
import messages from '../../Schedules.messages';
import './SchedulePreview.scss';

const { block, element } = bem('schedule-preview');

const SchedulePreviewPlaceholder = () => (
  <div className={block()}>
    <div className={element('placeholder')}>
      <Message as="p" className={element('placeholder-text')} message={messages.board.preview.placeholder} />
    </div>
  </div>
);

const ScheduleDescriptionText = ({ text, label }) => (
  <div className={element('description')}>
    <div className={element('description-label')}>{label}</div>
    <div className={element('description-text')}>{text}</div>
  </div>
);

const SchedulePreviewContent = ({ selectedScheduleInstance, venueId, openModal, cancelScheduleInstance }) => {
  const {
    schedule: {
      classTitle,
      durationInMinutes,
      instructorName,
      recurrence,
      recurring,
      status,
      id: scheduleId,
      lastChange,
    },
    isFuture,
    startingAt,
    status: instanceStatus,
    id: instanceId,
    recurrenceDate,
  } = selectedScheduleInstance;
  const isEditable = isFuture || status === DRAFT_STATUS;
  const isEditableInstance = isFuture && instanceStatus !== CANCELLED_STATUS;

  return (
    <div className={block()} data-cypress="schedule-preview-content">
      <div className={element('header')}>
        {classTitle}
        {isEditableInstance && recurring && (
          <SchedulePreviewActions
            openModal={openModal}
            activeSchedule={{
              scheduleName: classTitle,
              startDateTime: startingAt,
              instanceId,
              scheduleId,
              recurrenceDate,
            }}
            cancelScheduleInstance={cancelScheduleInstance}
            venueId={venueId}
          />
        )}
      </div>
      <div className={element('box')}>
        <div className={element('descriptions')}>
          <ScheduleDescriptionText
            text={<Message format="short" value={startingAt} />}
            label={<Message message={messages.board.preview.date} />}
          />
          <ScheduleDescriptionText
            text={<Message format="time" value={startingAt} />}
            label={<Message message={messages.board.preview.time} />}
          />
          <ScheduleDescriptionText
            text={
              <Message message={messages.board.preview.durationWithUnit} values={{ duration: durationInMinutes }} />
            }
            label={<Message message={messages.board.preview.duration} />}
          />
          <ScheduleDescriptionText
            text={recurring ? <ScheduleRecurrenceDays recurrence={recurrence} /> : <p>-</p>}
            label={<Message message={messages.board.preview.schedule} />}
          />
          <ScheduleDescriptionText
            text={instructorName || '-'}
            label={<Message message={messages.board.preview.instructor} />}
          />
          <ScheduleDescriptionText
            text={
              recurring ? (
                <Message message={messages.board.preview.recurring} />
              ) : (
                <Message message={messages.board.preview.oneTime} />
              )
            }
            label={<Message message={messages.board.preview.scheduleType} />}
          />
        </div>
      </div>
      {isEditable && (
        <div className={element('footer')}>
          <LastUpdate {...lastChange} />
          <Link
            to={makePath.schedulesEdit({
              venueId,
              id: scheduleId,
              startDate: startingAt,
            })}>
            <Message as={Button} message={messages.board.preview.edit} styled="primary" />
          </Link>
        </div>
      )}
    </div>
  );
};

const SchedulePreview = ({
  selectedScheduleInstance,
  venueId,
  hasPremiumPermission,
  openModal,
  cancelScheduleInstance,
}) => {
  if (_isEmpty(selectedScheduleInstance)) {
    return <SchedulePreviewPlaceholder />;
  }

  return (
    <SchedulePreviewContent
      selectedScheduleInstance={selectedScheduleInstance}
      venueId={venueId}
      hasPremiumPermission={hasPremiumPermission}
      openModal={openModal}
      cancelScheduleInstance={cancelScheduleInstance}
    />
  );
};

SchedulePreview.propTypes = {
  hasPremiumPermission: PropTypes.bool,
  ...manualSchedulesSummaryPreviewPropTypes,
};
SchedulePreviewContent.propTypes = manualSchedulesSummaryPreviewPropTypes;
ScheduleDescriptionText.propTypes = {
  text: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  label: PropTypes.node,
};

export default SchedulePreview;
